import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store';
import {MessageBox, Loading} from 'element-ui';
import ls from 'local-storage';
import modules from './modules';

Vue.use(VueRouter);

const routes = [
    {
        path: '/login', //登录
        name: 'login',
        component: () => import('@/views/passport/login'),
        meta: {
            requireAuth: false,
        },
    },
    {
        path: '/autologin', //自动登录
        name: 'autologin',
        component: () => import('@/views/passport/autoLogin'),
        meta: {
            requireAuth: false,
        },
    },
    {
        path: '/passport/register', //注册流程
        name: 'register',
        component: () => import('@/views/passport/register'),
        meta: {
            requireAuth: true,
        },
    },
    {
        path: '/aliPay', //支付宝支付
        component: () => import('@/views/manage/pay/aliPay'),
    },
    {
        path: '/',
        component: () => import('@/layout/default'),
        meta: {
            requireAuth: false,
        },
        children: [
            {
                path: '/', //首页
                name: 'index',
                component: () => import('@/views/index'),
            },
            {
                path: '404', // 404
                name: '404',
                component: () => import('@/views/error/404'),
            },
            {
                path: 'systemUpgrade', // 404
                name: 'systemUpgrade',
                component: () => import('@/views/error/systemUpgrade'),
            },
        ],
    },
    {
        path: '/help', //帮助中心
        component: () => import('@/layout/helpCenterLayout'),
        children: [
            {
                path: '',
                component: () => import('@/views/helpCenter'),
            },
            {
                path: ':id',
                component: () => import('@/views/helpCenter'),
                props: true,
            },
        ],
    },
    {
        path: '/pages/shareAlbum/shareAlbum',
        component: () => import('@/views/manage/albumManage/album/share'),
        meta: {title: '专辑分享'},
    },
    {
        path: '/manage', //管理中心
        component: () => import('@/layout/manage'),
        redirect: '/manage/dashboard',
        meta: {
            requireAuth: true,
        },
        children: [...modules],
    },

    {path: '*', redirect: '/404', meta: {breadcrumb: false}},
];

//使用push()、replace()进行导航时，不能重复导航到当前路由。
const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
    return originalPush.call(this, location).catch((err) => err);
};

const router = new VueRouter({
    mode: 'history',
    scrollBehavior: () => ({x: 0, y: 0}),
    routes,
});

/**
 * 保存邀请码
 */
function saveInvitationCode(query) {
    const { channelId, invitationCode } = query;
    if (channelId || invitationCode) {
        ls.set('channelId', channelId || null);
        ls.set('invitationCode', invitationCode || '');
    }
}
/**
 * 验证是否有数据中心权限
 */
async function validateTrendAccess(to) {
    const { trendConfig } = store.state;
    if (/trend/g.test(to.path) && !trendConfig) {
        await store.dispatch('getTrendConfig');
    }
}

/**
 * 加载系统基础信息
 */
async function loadBaseConfig() {
    const loading = Loading.service({
        lock: true,
        text: 'Loading',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)',
    });

    await store.dispatch('getBaseConfig');
    loading.close();
}


/**
 * 系统升级逻辑处理
 */
async function handleSystemUpgrade(to, next) {
    const config = store.state.config;
    if (config?.systemUpgrade === 'true') {
        if (to.path !== '/systemUpgrade') {
            next({ path: '/systemUpgrade' });
            return true;
        }
    } else if (to.path === '/systemUpgrade') {
        next({ path: '/' });
        return true;
    }
    return false;
}

/**
 * 验证用户是否已登录
 */
async function validateUserAuth(to, next) {
    const sign = ls.get('sign');
    if (to.path === '/login') {
        next();
        return true;
    }

    // 如果路由不需要登录权限，直接放行
    const requireAuth = to.matched.some((r) => r.meta.requireAuth);
    if (!sign && !requireAuth) {
        next();
        return true;
    }

    if (!sign) {
        next({ path: '/login', query: { redirect: to.fullPath } });
        return false;
    }
    try {
        // 加载认证企业信息
        await store.dispatch('getEnterpriseCertify', true);
        if (sign) {
            if (store.state.userInfo) return true;
            const userData = ls.get('userData');

            if (!store.state.userInfo && userData) {
                store.commit('SET_USERINFO', userData);
            } else {
                await store.dispatch('getUserInfo');
            }
            return true;
        }

        // 如果路由需要登录权限但用户未登录，跳转到登录页面
        if (requireAuth) {
            if (to.path !== '/login') {
                next({
                    path: '/login',
                    query: { redirect: to.fullPath },
                });
            }
            return false;
        }

        return true;
    } catch (error) {
        ls.remove('sign'); // 清除无效凭证
        next({ path: '/login' });
        return false;
    }
}

// 拦截器
router.beforeEach(async (to, from, next) => {
    try {
        // 自动登录页面直接放行
        if (to.path === '/autoLogin') return next();
        if (to.path === '/login') return next();

        // 验证是否是数据中心
        await validateTrendAccess(to);

        // 加载系统基础信息
        if (!store.state.config) {
            await loadBaseConfig();
        }

        // 保存邀请码
        saveInvitationCode(to.query);

        // 系统升级页面逻辑
        if (await handleSystemUpgrade(to, next)) {
            return;
        }
        // 验证用户登录状态
        const isAuthenticated = await validateUserAuth(to, next);
        if (!isAuthenticated) return;

        next();
    } catch (error) {
        next(false);
    }
});
//异常处理
router.onError(async (error) => {
    if (error.toString().indexOf('ChunkLoadError') === -1) {
        return;
    }

    const flag = await MessageBox.confirm('服务器版本已经更新，页面刷新后可以恢复正常使用', '提示', {
        confirmButtonText: '立即刷新',
        cancelButtonText: '取消',
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        center: true,
    }).catch(() => 'cancel');

    if (flag === 'cancel') {
        return;
    }

    window.location.reload();
});

export default router;
